import { useGetExamCenterProctorByIdQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { createContext, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type {
  ExamCenterProctorDetailsContextValue,
  ExamCenterProctorDetailsProviderProps,
} from './types';

const ExamCenterProctorDetailsContext = createContext<
  ExamCenterProctorDetailsContextValue | undefined
>(undefined);

export default function ExamCenterProctorDetailsProvider(
  props: Readonly<ExamCenterProctorDetailsProviderProps>,
) {
  const { examCenterCode, proctorId, children } = props;
  const { showSnackbar } = useSnackbar();

  const { data: getExamCenterProctorByIdResult, loading: isLoading } =
    useGetExamCenterProctorByIdQuery({
      variables: {
        getExamCenterProctorByIdInput: {
          examCenterProctorId: proctorId,
        },
      },
    });

  const proctor = getExamCenterProctorByIdResult?.ecGetExamCenterProctorById.examCenterProctor;

  const contextValue: ExamCenterProctorDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      proctor: proctor!,
    };
  }, [proctor]);

  useEffect(() => {
    if (!proctor && !isLoading) {
      showSnackbar({
        message: 'ไม่พบข้อมูลผู้คุมสอบดังกล่าว',
        variant: 'muted',
      });
    }
  }, [proctor, isLoading, showSnackbar]);

  if (isLoading) {
    return <Loading height='100%' />;
  }

  if (!proctor) {
    return <Navigate to='/:examCenterCode/exam-proctors/proctors' params={{ examCenterCode }} />;
  }

  return (
    <ExamCenterProctorDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </ExamCenterProctorDetailsContext.Provider>
  );
}

export { ExamCenterProctorDetailsContext };
